import React, { useState } from 'react';

import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useNotification } from '../../hooks/useNotification';
import { rateMessageMemories, type MessageMemoryRatingTarget } from '../../services/api';
import type { UserMemoryRating } from '../../types';

interface MessageMemoryFeedbackProps {
  target: MessageMemoryRatingTarget;
}

/** 👍/👎 on an assistant reply; the server applies it to the memories used for that reply. */
export const MessageMemoryFeedback: React.FC<MessageMemoryFeedbackProps> = ({ target }) => {
  const { t } = useTranslation();
  const { success, error: notifyError } = useNotification();
  const [rated, setRated] = useState<UserMemoryRating | null>(null);
  const [pending, setPending] = useState(false);

  const rate = async (rating: UserMemoryRating) => {
    if (pending || rated) return;
    setPending(true);
    const response = await rateMessageMemories(target, rating);

    if (response.success) {
      setRated(rating);
      success(t('chat.memoryFeedback.thanks'));
    } else {
      notifyError(response.error || t('settings.memory.errors.rateFailed'));
    }

    setPending(false);
  };

  const renderButton = (rating: UserMemoryRating, icon: React.ReactNode) => (
    <button
      type="button"
      onClick={() => void rate(rating)}
      disabled={pending || rated !== null}
      title={t(`chat.memoryFeedback.${rating}`)}
      className={`rounded p-0.5 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-default dark:hover:bg-slate-700 dark:hover:text-slate-300 ${
        rated === rating ? 'text-indigo-600 dark:text-indigo-400' : ''
      }`}
    >
      {icon}
    </button>
  );

  return (
    <>
      {renderButton('like', <ThumbsUp size={12} />)}
      {renderButton('dislike', <ThumbsDown size={12} />)}
    </>
  );
};
